import type {
  CentralChatMode,
  CentralChatWorkflowTile,
} from './CentralChatArrowSurface';
import type { CentralChatContextType } from './types';

// The one context whose backend stream reads `model` and `provider` off the
// request. `global`, `section` and `wordpress` run Emjae, whose model lives in
// Eve; `editor_article` and `service_page` pick their own server side. Sending
// a model to any of them only stamped an unused value on the conversation row.
export const MODEL_CHOOSING_CONTEXT_TYPES: ReadonlySet<CentralChatContextType> =
  new Set<CentralChatContextType>(['template_builder']);

export const CENTRAL_CHAT_MODES: CentralChatMode[] = [
  {
    id: 'plan',
    number: '01',
    label: 'plan',
    prompt: 'Build a short prioritized plan for improving this tenant.',
  },
  {
    id: 'inspect',
    number: '02',
    label: 'inspect',
    prompt: 'Inspect this workspace and tell me the highest-impact next step.',
  },
  {
    id: 'generate',
    number: '03',
    label: 'generate',
    prompt: 'Draft the next content action from the current page context.',
  },
  {
    id: 'google-ads',
    number: '04',
    label: 'google ads',
    // The "Google Ads:" prefix is what routes this to the ads stream
    // (_is_google_ads_request in chat_hub.py) — the chat transforms in
    // place instead of handing off to the standalone agent.
    prompt:
      'Google Ads: how did our campaigns perform in the last 30 days, and which one needs attention?',
  },
  {
    id: 'navigate',
    number: '05',
    label: 'navigate',
    prompt: 'Help me complete the current workflow and navigate to the right workspace.',
  },
];

export const CENTRAL_CHAT_WORKFLOW_TILES: CentralChatWorkflowTile[] = [
  {
    id: 'plan-content',
    mode: '01',
    modeLabel: 'plan',
    title: 'Plan content',
    description: 'Surface gaps and prioritize the next content opportunities.',
    prompt: 'Help me plan content opportunities for this workspace and take me to the content planner.',
  },
  {
    id: 'wordpress',
    mode: '02',
    modeLabel: 'inspect',
    title: 'Work in WordPress',
    description: 'Inspect recent posts and flag what needs attention.',
    prompt: 'Use WordPress context to inspect recent posts and suggest what needs attention.',
    contextType: 'wordpress',
  },
  {
    id: 'templates',
    mode: '03',
    modeLabel: 'generate',
    title: 'Tune templates',
    description: 'Take me to blog templates and improve a reusable content template.',
    prompt: 'Take me to blog templates and help me improve a reusable content template.',
  },
  {
    id: 'service-page',
    mode: '03',
    modeLabel: 'generate',
    title: 'Generate service page',
    description: 'Start a service page generation workflow with the current tenant.',
    prompt: 'Help me start a service page generation workflow.',
    contextType: 'service_page',
  },
  {
    id: 'keyword-research',
    mode: '01',
    modeLabel: 'plan',
    title: 'Research keywords',
    description: 'Prioritize search terms worth chasing for this tenant.',
    prompt: 'Take me to keyword research and help prioritize terms for this tenant.',
  },
  {
    id: 'google-ads',
    mode: '04',
    modeLabel: 'google ads',
    title: 'Check Google Ads',
    description: 'Live campaign performance, spend and what needs attention.',
    prompt:
      'Google Ads: how did our campaigns perform in the last 30 days, and which one needs attention?',
  },
  {
    id: 'google-ads-weekly',
    mode: '04',
    modeLabel: 'google ads',
    title: 'Weekly ads review',
    description: 'What the weekly sweep flagged, what it costs, what needs approval.',
    // "weekly run" is what routes this to the weekly stream
    // (is_weekly_management_request in chat_hub_ads_weekly.py) — a DB-only
    // read, so it answers without waiting on the Google Ads API.
    prompt:
      'Google Ads: what did the last weekly run find, and what needs my decision?',
  },
  {
    id: 'audit',
    mode: '02',
    modeLabel: 'inspect',
    title: 'Review technical SEO',
    description: 'Open the audit workspace and summarize what to inspect first.',
    prompt: 'Take me to the technical audit workspace and summarize what to inspect first.',
    recommended: true,
  },
];

export const CENTRAL_CHAT_HELP_TEXT = '⌘ K for commands  ·  Enter to send  ·  Shift+Enter for newline';

export const CENTRAL_CHAT_MODEL = import.meta.env.VITE_CENTRAL_CHAT_MODEL || 'accounts/fireworks/models/kimi-k2p6';
export const CENTRAL_CHAT_PROVIDER = import.meta.env.VITE_CENTRAL_CHAT_PROVIDER || 'fireworks';
